import { camelCase, compact } from "es-toolkit";
import type { MetaobjectBadge } from "~/lib/badges/types";

interface MetaobjectField {
  key: string;
  value?: string | null | undefined;
}

export function normalizeTag(tag: string): string {
  return tag.trim().normalize("NFKC").toLowerCase();
}

export function parseTagList(value: string): string[] {
  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return compact(parsed.map((t) => normalizeTag(String(t))));
      }
    } catch {
      // fall through to csv
    }
  }
  return compact(value.split(",").map(normalizeTag));
}

export function parseCsvOrJson(value: string): string[] {
  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return compact(parsed.map((s) => String(s).trim()));
      }
    } catch {
      // fall through to csv
    }
  }
  return compact(value.split(",").map((s) => s.trim()));
}

export function parseBadgeFields(fields: MetaobjectField[], id: string): MetaobjectBadge {
  const raw: Record<string, unknown> = { id };

  for (const { key, value } of fields) {
    if (!value) {
      if (
        key === "criteria_collections" ||
        key === "criteria_tags" ||
        key === "criteria_tags_exclude"
      ) {
        raw[camelCase(key)] = [];
      }
      continue;
    }

    switch (key) {
      case "start_date_time":
        raw.startDateTime = Date.parse(value) || undefined;
        break;
      case "end_date_time":
        raw.endDateTime = Date.parse(value) || undefined;
        break;
      case "criteria_collections":
        raw.criteriaCollections = parseCsvOrJson(value);
        break;
      case "criteria_tags":
        raw.criteriaTags = parseTagList(value);
        break;
      case "criteria_tags_exclude":
        raw.criteriaTagsExclude = parseTagList(value);
        break;
      case "priority": {
        const parsed = Number(value);
        raw.priority = Number.isFinite(parsed) ? parsed : 0;
        break;
      }
      default:
        raw[camelCase(key)] = value;
    }
  }

  return {
    id: String(raw.id ?? ""),
    label: String(raw.label ?? ""),
    type: String(raw.type ?? ""),
    style: String(raw.style ?? ""),
    icon: (raw.icon as MetaobjectBadge["icon"]) ?? null,
    priority: Number(raw.priority ?? 0),
    startDateTime: raw.startDateTime as number | undefined,
    endDateTime: raw.endDateTime as number | undefined,
    criteriaCollections: (raw.criteriaCollections as string[]) ?? [],
    criteriaTags: (raw.criteriaTags as string[]) ?? [],
    criteriaTagsExclude: (raw.criteriaTagsExclude as string[]) ?? [],
    criteriaStockStatus:
      (raw.criteriaStockStatus as MetaobjectBadge["criteriaStockStatus"]) ?? "Any stock status",
  };
}
